import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useGetRatePlanCreateSchema } from '~/hooks/apis/reservation/usePropertyRatePlans';

import { alwaysActionable } from '../types';

import { createRatePlans } from './createRatePlans';
import {
  buildDefaultRatePlanDraft,
  getDefaultFlashRatePlanName,
  getRatePlanDraftsValidationError,
  isCreatedRatePlanDraft,
} from './utils';
import type { FlashRatePlanDraft } from './utils';

export interface UseRatePlanStepParams {
  isRatePlanStep: boolean;
  vendorId: string;
}

/**
 * Owns the Rate Plan step (4): the Flash rate plan drafts a CM builds up (add/remove/expand),
 * their validation, and creating them once "Next" is pressed.
 */
export function useRatePlanStep({ isRatePlanStep, vendorId }: UseRatePlanStepParams) {
  const [ratePlanDrafts, setRatePlanDrafts] = useState<Array<FlashRatePlanDraft>>([]);
  const [expandedRatePlanDraftIds, setExpandedRatePlanDraftIds] = useState<Array<string>>([]);
  const [createdRatePlans, setCreatedRatePlans] = useState<Array<App.RatePlan>>([]);
  const [isCreatingRatePlan, setIsCreatingRatePlan] = useState(false);
  const ratePlanDraftCounter = useRef(0);
  const defaultRatePlanDraftValues = useRef({
    ratePlanCancellationPolicy: '',
    ratePlanName: getDefaultFlashRatePlanName(),
  });

  const {
    data: ratePlanCreateSchema,
    isError: hasRatePlanCreateSchemaError,
    isFetching: isLoadingRatePlanCreateSchema,
    refetch: refetchRatePlanCreateSchema,
  } = useGetRatePlanCreateSchema(isRatePlanStep);

  const createdRatePlanIds = useMemo(
    () => createdRatePlans.map((ratePlan) => ratePlan.id).filter(Boolean),
    [createdRatePlans],
  );
  const areRatePlanDraftsComplete = ratePlanDrafts.length > 0 && !getRatePlanDraftsValidationError(ratePlanDrafts);
  const isRatePlanStepReadOnly =
    ratePlanDrafts.length > 0 && ratePlanDrafts.every((draft) => !!draft.createdRatePlanId);
  const isRatePlanStepNextDisabled =
    isRatePlanStep &&
    (isCreatingRatePlan || !areRatePlanDraftsComplete || isLoadingRatePlanCreateSchema || hasRatePlanCreateSchemaError);

  function generateRatePlanDraftId() {
    ratePlanDraftCounter.current += 1;
    return `rate-plan-draft-${ratePlanDraftCounter.current}`;
  }

  const initializeRatePlanDrafts = useCallback(
    ({
      ratePlanCancellationPolicy = '',
      ratePlanName = getDefaultFlashRatePlanName(),
    }: {
      ratePlanCancellationPolicy?: string;
      ratePlanName?: string;
    } = {}) => {
      defaultRatePlanDraftValues.current = {
        ratePlanCancellationPolicy,
        ratePlanName,
      };

      const draft = buildDefaultRatePlanDraft({
        draftId: generateRatePlanDraftId(),
        ratePlanCancellationPolicy,
        ratePlanName,
      });

      setRatePlanDrafts([draft]);
      setExpandedRatePlanDraftIds([draft.draftId]);
      setCreatedRatePlans([]);
    },
    [],
  );

  useEffect(() => {
    if (isRatePlanStep && ratePlanDrafts.length === 0) {
      initializeRatePlanDrafts();
    }
  }, [initializeRatePlanDrafts, isRatePlanStep, ratePlanDrafts.length]);

  function updateRatePlanDraft(draftId: string, updates: Partial<FlashRatePlanDraft>) {
    if (isRatePlanStepReadOnly) {
      return;
    }

    setRatePlanDrafts((prev) =>
      prev.map((draft) => {
        if (draft.draftId !== draftId || isCreatedRatePlanDraft(draft)) {
          return draft;
        }

        return { ...draft, ...updates };
      }),
    );
  }

  function addRatePlanDraft() {
    if (isRatePlanStepReadOnly) {
      return;
    }

    const { ratePlanCancellationPolicy, ratePlanName } = defaultRatePlanDraftValues.current;
    const draftId = generateRatePlanDraftId();

    setRatePlanDrafts((prev) => {
      const draftNumber = prev.length + 1;
      const uniqueRatePlanName = draftNumber === 1 ? ratePlanName : `${ratePlanName} ${draftNumber}`;

      return prev.concat(
        buildDefaultRatePlanDraft({
          draftId,
          ratePlanCancellationPolicy,
          ratePlanName: uniqueRatePlanName,
        }),
      );
    });
    setExpandedRatePlanDraftIds((prev) => Array.from(new Set(prev.concat(draftId))));
  }

  function removeRatePlanDraft(draftId: string) {
    if (isRatePlanStepReadOnly || ratePlanDrafts.length <= 1) {
      return;
    }

    const removedDraft = ratePlanDrafts.find((draft) => draft.draftId === draftId);

    if (!removedDraft || isCreatedRatePlanDraft(removedDraft)) {
      return;
    }

    setRatePlanDrafts((prev) => prev.filter((draft) => draft.draftId !== draftId));
    setExpandedRatePlanDraftIds((prev) => prev.filter((id) => id !== draftId));
  }

  function toggleRatePlanDraftExpanded(draftId: string, expanded: boolean) {
    setExpandedRatePlanDraftIds((prev) =>
      expanded ? Array.from(new Set(prev.concat(draftId))) : prev.filter((id) => id !== draftId),
    );
  }

  async function createRatePlansFromDrafts(): Promise<{
    createdRatePlans?: Array<App.RatePlan>;
    errorMessage?: string;
    nextDrafts: Array<FlashRatePlanDraft>;
    success: boolean;
  }> {
    setIsCreatingRatePlan(true);

    const result = await createRatePlans({ ratePlanDrafts, vendorId });

    setIsCreatingRatePlan(false);
    setRatePlanDrafts(result.nextDrafts);

    if (result.createdRatePlans) {
      setCreatedRatePlans(result.createdRatePlans);
    }

    return {
      createdRatePlans: result.createdRatePlans,
      errorMessage: result.errorMessage,
      nextDrafts: result.nextDrafts,
      success: result.success,
    };
  }

  // Clears createdRatePlanId/createdRatePlans -- the survivors that lock this step read-only
  // (`isRatePlanStepReadOnly`) after a rolled-back attempt.
  function resetAfterFailedSubmission() {
    setRatePlanDrafts((prev) =>
      prev.map((draft) => (draft.createdRatePlanId ? { ...draft, createdRatePlanId: undefined } : draft)),
    );
    setCreatedRatePlans([]);
  }

  function reset() {
    setRatePlanDrafts([]);
    setExpandedRatePlanDraftIds([]);
    setCreatedRatePlans([]);
    setIsCreatingRatePlan(false);
    ratePlanDraftCounter.current = 0;
  }

  return {
    addRatePlanDraft,
    areRatePlanDraftsComplete,
    resetAfterFailedSubmission,
    createdRatePlanIds,
    createdRatePlans,
    createRatePlansFromDrafts,
    expandedRatePlanDraftIds,
    hasRatePlanCreateSchemaError,
    initializeRatePlanDrafts,
    isCreatingRatePlan,
    isLoadingRatePlanCreateSchema,
    isRatePlanStepNextDisabled,
    isRatePlanStepReadOnly,
    isSubmissionFailureActionable: alwaysActionable,
    ratePlanCreateSchema,
    ratePlanDrafts,
    refetchRatePlanCreateSchema,
    removeRatePlanDraft,
    reset,
    setIsCreatingRatePlan,
    toggleRatePlanDraftExpanded,
    updateRatePlanDraft,
  };
}
